import { createAction, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import dayjs, { Dayjs } from "dayjs";
import { IAppointmentType, patients, schedulingV2 } from "lobbie";
import { IFormHelper } from "src/components/pages/staff/patients/dep/AccountPatientForms";
import { buildResourcePathForCurrentUser } from "src/redux/ResourceBuilder";
import type { RootState } from "src/redux/store";

export interface AppointmentDrawerState {
    show: boolean;
    onAppointmentUpdate: ((schedulerConfigIds: number[]) => void) | null;
    appointmentId?: number;
    existingPatientAssignedToAppointment?: Pick<
        patients.IAccountPatient,
        "accountPatientId" | "firstName" | "lastName"
    >;
    ghostAppointmentSegment?: schedulingV2.IAppointmentSeriesSegmentInfo;
    appointmentType: IAppointmentType | undefined;
    selectedAppointmentTime: Dayjs | undefined;
    buildingContext?: schedulingV2.IAppointmentBuildingContext;
    isFetchingPatientForms?: boolean;
    isFetchingPatient?: boolean;
}

export const initialStateAppointmentDrawer: AppointmentDrawerState = {
    show: false,
    onAppointmentUpdate: null,
    appointmentType: undefined,
    selectedAppointmentTime: undefined,
    isFetchingPatientForms: false,
    isFetchingPatient: false,
};
/*******************************************/
/*
    These APIs probably belong in RTK, but without knowing for sure they are available to the slice since they are part of slice managed state.
*/
export const setSelectedPatientForContext = createAsyncThunk(
    "appointmentDrawer/setSelectedPatientForContext",
    async (patientId: number, thunkAPI) => {
        const appState = thunkAPI.getState() as RootState;
        const url = buildResourcePathForCurrentUser(appState, `patients/${patientId}`);
        try {
            const response = await axios.get<patients.IAccountPatient>(url!, {
                withCredentials: true,
            });
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError;
            return thunkAPI.rejectWithValue(axiosError);
        }
    },
);

export const setSelectedPatientFormsForContext = createAsyncThunk(
    "appointmentDrawer/setSelectedPatientFormsForContext",
    async (patientId: number, thunkAPI) => {
        const appState = thunkAPI.getState() as RootState;
        const url = buildResourcePathForCurrentUser(
            appState,
            `account_patients/forms/${patientId}`,
        );
        try {
            const response = await axios.get<IFormHelper[]>(url!, {
                withCredentials: true,
            });
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError;
            return thunkAPI.rejectWithValue(axiosError);
        }
    },
);

export const setSelectedPatientAndFormsForContext = createAsyncThunk(
    "appointmentDrawer/setSelectedPatientAndFormsForContext",
    async (patientId: number, thunkAPI) => {
        await thunkAPI.dispatch(setSelectedPatientForContext(patientId));
        await thunkAPI.dispatch(setSelectedPatientFormsForContext(patientId));
    },
);

export const setPatientForExistingAppointment = createAction<
    Pick<patients.IAccountPatient, "accountPatientId" | "firstName" | "lastName">
>("appointmentDrawer/setPatientForExistingAppointment");
/*******************************************/

const appointmentDrawerSlice = createSlice({
    name: "appointmentDrawer",
    initialState: initialStateAppointmentDrawer,
    reducers: {
        showAppointmentDrawer: (
            state,
            action: PayloadAction<Omit<AppointmentDrawerState, "show">>,
        ) => {
            return {
                ...action.payload,
                show: true,
                onAppointmentUpdate:
                    action.payload.onAppointmentUpdate ?? state.onAppointmentUpdate,
            };
        },
        setAppointmentDrawerCallbacks: (
            state,
            action: PayloadAction<{
                onAppointmentUpdate: (schedulerConfigIds: number[]) => void;
            }>,
        ) => {
            state.onAppointmentUpdate = action.payload.onAppointmentUpdate;
        },
        showGhostAppointmentSegment: (
            state,
            action: PayloadAction<{
                ghostDetails: schedulingV2.IAppointmentSeriesSegmentInfo;
                onAppointmentUpdate: (schedulerConfigIds: number[]) => void;
            }>,
        ) => {
            state.show = true;
            state.ghostAppointmentSegment = action.payload.ghostDetails;
            state.appointmentId = undefined;
            state.onAppointmentUpdate = action.payload.onAppointmentUpdate;
        },
        hideAppointmentDrawer: () => initialStateAppointmentDrawer,
        setAppointmentType: (state, action: PayloadAction<IAppointmentType>) => {
            state.appointmentType = action.payload;
        },
        setAppointmentSelectedTime: (state, action: PayloadAction<Dayjs>) => {
            state.selectedAppointmentTime = action.payload;
        },
        loadExistingAppt: (state, action: PayloadAction<schedulingV2.IAppointmentDrawer>) => {
            if (action.payload.appointmentId) {
                state.appointmentId = action.payload.appointmentId;
            }
            state.appointmentType = action.payload.appointmentType;
            state.selectedAppointmentTime = dayjs(action.payload.startUnix);
        },
        loadGhostAppt: (
            state,
            action: PayloadAction<{
                appointmentType: IAppointmentType;
                selectedTime: Dayjs;
            }>,
        ) => {
            state.appointmentType = action.payload.appointmentType;
            state.selectedAppointmentTime = action.payload.selectedTime;
        },
        // The only case where we need to update the buildingContext outside of show because accountPatient can change
        setSelectedPatientFormsForAppointment: (
            state: AppointmentDrawerState,
            action: PayloadAction<IFormHelper[] | undefined>,
        ): void => {
            if (state.buildingContext) {
                state.buildingContext.selectedPatientForms = action.payload;
            }
        },
        setSelectedPatientForAppointment: (
            state: AppointmentDrawerState,
            action: PayloadAction<patients.IAccountPatient | undefined>,
        ): void => {
            if (state.buildingContext) {
                state.buildingContext.selectedPatient = action.payload;
            }
        },
        setSchedulerConfigId: (
            state: AppointmentDrawerState,
            action: PayloadAction<number>,
        ): void => {
            if (state.buildingContext) {
                state.buildingContext.schedulerConfigId = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(setSelectedPatientForContext.fulfilled, (state, action) => {
            state.isFetchingPatient = false;
            if (state.buildingContext) {
                state.buildingContext.selectedPatient = action.payload;
            }
        });
        builder.addCase(setSelectedPatientForContext.pending, (state) => {
            state.isFetchingPatient = true;
        });
        builder.addCase(setSelectedPatientForContext.rejected, (state) => {
            state.isFetchingPatient = false;
            console.error("Failed to fetch selected patient for appointment context");
            if (state.buildingContext) {
                state.buildingContext.selectedPatient = undefined;
            }
        });
        builder.addCase(setSelectedPatientFormsForContext.fulfilled, (state, action) => {
            state.isFetchingPatientForms = false;
            if (state.buildingContext) {
                state.buildingContext.selectedPatientForms = action.payload;
            }
        });
        builder.addCase(setSelectedPatientFormsForContext.pending, (state) => {
            state.isFetchingPatientForms = true;
        });
        builder.addCase(setSelectedPatientFormsForContext.rejected, (state) => {
            state.isFetchingPatientForms = false;
            console.error("Failed to fetch selected patient forms for appointment context");
            if (state.buildingContext) {
                state.buildingContext.selectedPatientForms = undefined;
            }
        });
        builder.addCase(setPatientForExistingAppointment, (state, action) => {
            state.existingPatientAssignedToAppointment = action.payload;
        });
    },
});

export const isFetchingPatientForAppointment = (state: RootState) =>
    state.appointmentDrawer.isFetchingPatient;
export const selectedPatientForAppointment = (state: RootState) =>
    state.appointmentDrawer.buildingContext?.selectedPatient;
export const isFetchingPatientFormsForAppointment = (state: RootState) =>
    state.appointmentDrawer.isFetchingPatientForms;
export const selectedPatientFormsForAppointment = (state: RootState) =>
    state.appointmentDrawer.buildingContext?.selectedPatientForms;
export const isAppointmentSelected = (state: RootState) =>
    state.appointmentDrawer.appointmentId !== undefined;
export const isGhostAppointmentSelected = (state: RootState) =>
    state.appointmentDrawer.ghostAppointmentSegment !== undefined;
export const patientAssignedToExistingAppointment = (state: RootState) =>
    state.appointmentDrawer.existingPatientAssignedToAppointment;

export const {
    showAppointmentDrawer,
    setAppointmentDrawerCallbacks,
    showGhostAppointmentSegment,
    hideAppointmentDrawer,
    setAppointmentType,
    setAppointmentSelectedTime,
    loadExistingAppt,
    loadGhostAppt,
    setSelectedPatientForAppointment,
    setSelectedPatientFormsForAppointment,
    setSchedulerConfigId,
} = appointmentDrawerSlice.actions;
export default appointmentDrawerSlice;
